import { requestJson } from "@/lib/api";

export interface TaxExpense {
  description: string;
  mode: "PERCENT" | "FIXO";
  percentValue: number | null;
  percentBase: "PRECO_FINAL" | "APOS_COMISSAO_CLIENTE" | null;
  fixedValue: number | null;
  fixedScope: "POR_PRODUTO" | "POR_PEDIDO" | "POR_ENTREGA" | null;
  fixedUnitId: string | null;
  fixedUnitName: string | null;
}

export interface NegotiationVersion {
  id: string;
  scopeType: "CLIENTE" | "UNIDADE_ENTREGA";
  customerId: string;
  deliveryUnitId: string | null;
  validFrom: string;
  validTo: string | null;
  status: "FUTURA" | "VIGENTE" | "ENCERRADA";
  clientCommissionPercent: number | null;
  intermediaryCommissionPercent: number | null;
  intermediaryCommissionBase: "PRECO_FINAL" | "APOS_COMISSAO_CLIENTE" | null;
  targetMarginPercent: number | null;
  minimumMarginPercent: number | null;
  taxListMode: "NAO_CONFIGURADA" | "HERDAR" | "DEFINIDA";
  taxes: TaxExpense[];
  note: string | null;
  version: number;
  updatedAt: string;
}

export interface ResolvedValue {
  value: number | null;
  origin: "UNIDADE" | "CLIENTE" | "PADRAO" | "NAO_CADASTRADA";
  explicitZero: boolean;
}

export interface EffectiveNegotiation {
  customerId: string;
  deliveryUnitId: string | null;
  date: string;
  configured: boolean;
  message: string | null;
  clientCommissionPercent: ResolvedValue;
  intermediaryCommissionPercent: ResolvedValue;
  intermediaryCommissionBase: string | null;
  intermediaryCommissionBaseOrigin: string;
  intermediaryIncompleteAfterClientCommission: boolean;
  targetMarginPercent: ResolvedValue;
  minimumMarginPercent: ResolvedValue;
  taxListOrigin: string;
  taxListState: string;
  taxes: TaxExpense[];
}

export interface NegotiationHistoryEntry {
  id: string;
  conditionId: string | null;
  scopeType: string;
  customerId: string;
  deliveryUnitId: string | null;
  scopeLabel: string | null;
  changedAt: string;
  changedBy: string;
  action: "CREATE" | "UPDATE" | "DELETE";
  summary: string | null;
  beforeJson: string | null;
  afterJson: string | null;
}

export interface ConditionVersionInput {
  validFrom: string;
  validTo: string | null;
  clientCommissionPercent: number | null;
  intermediaryCommissionPercent: number | null;
  intermediaryCommissionBase: string | null;
  targetMarginPercent: number | null;
  minimumMarginPercent: number | null;
  taxListMode: string;
  taxes: TaxExpense[];
  note: string | null;
  expectedVersion: number | null;
}

/** Base por escopo: cliente ou exceção de unidade de entrega. */
function base(customerId: string, unitId?: string | null): string {
  const root = `/api/v1/admin/customers/${customerId}`;
  return unitId ? `${root}/delivery-units/${unitId}/negotiation` : `${root}/negotiation`;
}

export function fetchNegotiationVersions(customerId: string, unitId?: string | null): Promise<NegotiationVersion[]> {
  return requestJson<NegotiationVersion[]>(base(customerId, unitId), { method: "GET" });
}

export function fetchEffectiveNegotiation(
  customerId: string,
  unitId?: string | null,
  date?: string,
): Promise<EffectiveNegotiation> {
  const q = date ? `?date=${date}` : "";
  return requestJson<EffectiveNegotiation>(`${base(customerId, unitId)}/effective${q}`, { method: "GET" });
}

export function fetchNegotiationHistory(
  customerId: string,
  unitId?: string | null,
): Promise<NegotiationHistoryEntry[]> {
  return requestJson<NegotiationHistoryEntry[]>(`${base(customerId, unitId)}/history`, { method: "GET" });
}

export function createNegotiationVersion(
  customerId: string,
  unitId: string | null,
  input: ConditionVersionInput,
): Promise<NegotiationVersion> {
  return requestJson<NegotiationVersion>(base(customerId, unitId), { method: "POST", body: input });
}

export function updateNegotiationVersion(
  customerId: string,
  unitId: string | null,
  conditionId: string,
  input: ConditionVersionInput,
): Promise<NegotiationVersion> {
  return requestJson<NegotiationVersion>(`${base(customerId, unitId)}/${conditionId}`, { method: "PUT", body: input });
}

export function deleteNegotiationVersion(
  customerId: string,
  unitId: string | null,
  conditionId: string,
): Promise<void> {
  return requestJson<void>(`${base(customerId, unitId)}/${conditionId}`, { method: "DELETE" });
}
